import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AINewSettingsTabs from "./AINewSettingsTabs";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import useSubscriptions from "@/hooks/useSubscriptions";
import { Severity } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
interface QnAItem {
  id: number;
  question: string;
  answer: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

const EMPTY_FORM = { question: "", answer: "", isActive: true, sortOrder: 0 };

export default function AINewSettingsQnA() {
  const { id } = useParams();
  const api = useApi();
  const toast = useToastAlert();
  const { subscription } = useSubscriptions();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [items, setItems] = useState<QnAItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState<QnAItem | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.messaging.get("BotQnA");
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setShowImport(false);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(item: QnAItem) {
    setEditTarget(item);
    setForm({
      question: item.question,
      answer: item.answer,
      isActive: item.isActive,
      sortOrder: item.sortOrder
    });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.question.trim()) {
      toast.displayToast(Severity.Error, "Question is required.");
      return;
    }
    if (!form.answer.trim()) {
      toast.displayToast(Severity.Error, "Answer is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        question: form.question.trim(),
        answer: form.answer.trim(),
        isActive: form.isActive,
        sortOrder: form.sortOrder
      };
      const res = editTarget
        ? await api.messaging.put(`BotQnA/${editTarget.id}`, payload)
        : await api.messaging.post("BotQnA", payload);
      const data = await res.json();
      if (res.ok) {
        toast.displayToast(
          Severity.Success,
          editTarget ? "Q&A updated and re-indexed." : "Q&A saved and indexed."
        );
        setShowForm(false);
        setEditTarget(null);
        await load();
      } else {
        toast.displayToast(Severity.Error, data?.error ?? "Save failed.");
      }
    } catch {
      toast.displayToast(Severity.Error, "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: QnAItem) {
    if (
      !window.confirm(
        `Delete "${item.question}"?\n\nThis will also remove it from the AI search index.`
      )
    )
      return;
    try {
      const res = await api.messaging.delete(`BotQnA/${item.id}`);
      if (res.ok) {
        toast.displayToast(Severity.Success, "Q&A deleted and removed from index.");
        await load();
      } else {
        toast.displayToast(Severity.Error, "Delete failed.");
      }
    } catch {
      toast.displayToast(Severity.Error, "Delete failed.");
    }
  }

  async function handleToggle(item: QnAItem) {
    try {
      const res = await api.messaging.put(`BotQnA/${item.id}`, {
        question: item.question,
        answer: item.answer,
        isActive: !item.isActive,
        sortOrder: item.sortOrder
      });
      if (res.ok) {
        await load();
      } else {
        toast.displayToast(Severity.Error, "Failed to update Q&A.");
      }
    } catch {
      toast.displayToast(Severity.Error, "Failed to update Q&A.");
    }
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      setImportText(text);
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  async function handleImport() {
    let parsed: { question: string; answer: string }[];
    try {
      parsed = JSON.parse(importText);
      if (!Array.isArray(parsed)) throw new Error("Must be an array");
    } catch {
      toast.displayToast(
        Severity.Error,
        "Invalid JSON — must be an array of { question, answer } objects."
      );
      return;
    }

    setSaving(true);
    try {
      const res = await api.messaging.post("BotQnA/import", parsed);
      const data = await res.json();
      if (res.ok) {
        toast.displayToast(
          Severity.Success,
          `Imported ${data.created} Q&A items (${data.skipped} skipped). Indexing in background.`
        );
        setShowImport(false);
        setImportText("");
        await load();
      } else {
        toast.displayToast(Severity.Error, data?.error ?? "Import failed.");
      }
    } catch {
      toast.displayToast(Severity.Error, "Import failed.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id ?? ""} />

      <div className="max-w-4xl mx-auto w-full px-4 pb-10 pt-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Q&amp;A Knowledge</h2>
            <p className="text-sm text-slate-500 mt-0.5 max-w-xl">
              Type question &amp; answer pairs directly. Each pair is embedded into the AI search
              index so the bot finds and uses them when customers ask matching questions. This is the
              fastest way to add knowledge — no files or web scraping needed.
            </p>
          </div>
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => { setShowImport((v) => !v); setShowForm(false); setEditTarget(null); }}
              className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              Import JSON
            </button>
            <button
              onClick={openCreate}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 whitespace-nowrap"
            >
              + Add Q&amp;A
            </button>
          </div>
        </div>

        {/* How it works callout */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5 text-sm text-blue-800 space-y-1">
          <p>
            <strong>How it works:</strong> Q&amp;A pairs are vectorized and stored in the AI search
            index. When a customer asks a question, the bot searches the index for the closest
            matching answer.
          </p>
          <p className="text-blue-700">
            <strong>vs Knowledge Overrides:</strong> Overrides are{" "}
            <em>always injected verbatim before this index is searched</em> — use them for guaranteed
            policy facts, timed closures, or holiday hours.{" "}
            <Link
              to={`/ai-new/settings/knowledge-overrides/${id ?? ""}`}
              className="underline hover:text-blue-900"
            >
              Manage overrides →
            </Link>
          </p>
          <p className="text-blue-700">
            <strong>vs Knowledge Base articles:</strong> The KB is customer-facing self-service
            content {subscription?.enableKnowledgeBase ? (
              <>— you have it enabled.{" "}
                <Link to="/kb" className="underline hover:text-blue-900">Go to Knowledge Base →</Link>
              </>
            ) : (
              "(requires KB subscription — contact us to enable)"
            )}. Q&amp;A pairs here are AI training data only — customers never see them directly.
          </p>
        </div>

        {/* Import panel */}
        {showImport && (
          <div className="border border-slate-200 rounded-xl p-4 mb-5 bg-slate-50 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-slate-700">Import Q&amp;A pairs</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 text-xs text-primary border border-cornflower-blue-300 rounded-lg px-2.5 py-1 hover:bg-primary-bg"
              >
                <i className="fa fa-upload text-xs" /> Upload .json file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                className="sr-only"
                onChange={handleFileUpload}
              />
            </div>
            <pre className="text-xs text-slate-500 bg-white border rounded p-2">{`[
  { "question": "What is your return policy?", "answer": "30-day returns on all items." },
  { "question": "Do you offer free shipping?",  "answer": "Yes, on orders over $50."    }
]`}</pre>
            <textarea
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
              rows={6}
              placeholder='Paste JSON here or upload a .json file above…'
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
            />
            <div className="flex gap-2">
              <button
                onClick={handleImport}
                disabled={saving || !importText.trim()}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Importing\u2026" : "Import"}
              </button>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportText("");
                }}
                className="px-4 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500">
            <div className="text-3xl mb-2">💬</div>
            <div className="font-medium mb-1">No Q&amp;A pairs yet</div>
            <div className="text-sm">
              Add your first question and answer — or import a JSON file from the &quot;Import
              JSON&quot; button above.
            </div>
          </div>
        )}

        {/* Items list */}
        {items.length > 0 && (
          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <div
                key={item.id}
                className={`bg-white border rounded-xl px-5 py-4 shadow-sm ${
                  item.isActive ? "border-slate-200" : "border-slate-100 opacity-60"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 text-sm truncate">
                      {item.question}
                    </div>
                    <div className="text-slate-500 text-xs mt-1 line-clamp-2">{item.answer}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleToggle(item)}
                      aria-label={item.isActive ? "Deactivate" : "Activate"}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                        item.isActive ? "bg-blue-600" : "bg-slate-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                          item.isActive ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => openEdit(item)}
                      className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="text-sm text-red-500 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50"
                      aria-label={`Delete ${item.question}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slide-over form drawer */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/30"
            onClick={() => setShowForm(false)}
            aria-hidden="true"
          />
          <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-base font-semibold text-slate-800">
                {editTarget ? "Edit Q&A" : "New Q&A Pair"}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-slate-400 hover:text-slate-600 text-xl"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="flex flex-col gap-5 px-6 py-6 flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Question <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.question}
                  onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                  placeholder="e.g. What is your return policy?"
                  maxLength={1000}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Answer <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.answer}
                  onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                  placeholder="Enter the answer the bot should give..."
                  maxLength={4000}
                  rows={6}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y"
                />
                <p className="text-xs text-slate-400 mt-1">{form.answer.length}/4000 chars</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                  aria-label={form.isActive ? "Deactivate" : "Activate"}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    form.isActive ? "bg-blue-600" : "bg-slate-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                      form.isActive ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
                <div>
                  <span className="text-sm text-slate-700">
                    {form.isActive ? "Enabled — bot will use this pair" : "Disabled — paused, not deleted"}
                  </span>
                  {!form.isActive && (
                    <p className="text-xs text-slate-400 mt-0.5">Bot skips this pair until you re-enable it. Nothing is removed.</p>
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex gap-3 justify-end">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? "Saving\u2026" : editTarget ? "Save Changes" : "Add Q&A"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
